import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Plus, Shield, Zap, Crosshair, Radio, Wifi, Lock, Activity, Power, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

const WEAPON_ICON = {
  directed_energy: Zap,
  anti_drone_gun: Crosshair,
  kinetic_interceptor: Target,
  net_capture: Shield,
  anti_drone_missile: Target,
  rf_jamming: Radio,
  gps_spoofing: Wifi,
  comm_disruption: Radio,
  cyber_takeover: Lock,
};

const STATUS_STYLES = {
  online: { dot: 'bg-emerald-500', text: 'text-emerald-400', label: 'ONLINE' },
  offline: { dot: 'bg-zinc-600', text: 'text-zinc-500', label: 'OFFLINE' },
  cooldown: { dot: 'bg-amber-500 animate-pulse', text: 'text-amber-400', label: 'COOLDOWN' },
  maintenance: { dot: 'bg-orange-500', text: 'text-orange-400', label: 'MAINT.' },
};

function WeaponSystemsPanel({ weapons, onToggle }) {
  if (!weapons.length) return null;
  const hard = weapons.filter(w => w.category === 'hard_kill');
  const soft = weapons.filter(w => w.category === 'soft_kill');

  const renderCard = (w) => {
    const Icon = WEAPON_ICON[w.measure_type] || Zap;
    const status = STATUS_STYLES[w.status] || STATUS_STYLES.offline;
    const ammoPct = w.ammo_max > 0 ? (w.ammo_current / w.ammo_max) * 100 : 0;
    const lowAmmo = ammoPct < 25;
    const isHard = w.category === 'hard_kill';
    const accent = isHard ? 'border-red-900/40 hover:border-red-700/60' : 'border-sky-900/40 hover:border-sky-700/60';
    const iconColor = isHard ? 'text-red-400' : 'text-sky-400';

    return (
      <div
        key={w.id}
        className={`bg-zinc-950/80 border ${accent} rounded-sm p-4 transition-colors`}
        data-testid={`weapon-${w.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon size={20} className={iconColor} />
            <div>
              <div className="text-[10px] font-mono uppercase text-zinc-500 leading-none">{w.short_name}</div>
              <div className="text-sm font-bold uppercase tracking-tight leading-tight">{w.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5" data-testid={`weapon-status-${w.id}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            <span className={`text-[10px] font-mono ${status.text}`}>{status.label}</span>
          </div>
        </div>

        <div className="space-y-1.5 mb-3">
          <div className="flex items-center justify-between text-[11px] font-mono">
            <span className="text-zinc-500 uppercase">{w.ammo_unit}</span>
            <span className={lowAmmo ? 'text-red-400' : 'text-zinc-300'}>
              {w.ammo_current.toLocaleString()} / {w.ammo_max.toLocaleString()}
            </span>
          </div>
          <div className="h-1 bg-zinc-900 rounded-sm overflow-hidden">
            <div
              className={`h-full transition-all ${lowAmmo ? 'bg-red-500' : isHard ? 'bg-red-500/70' : 'bg-sky-500/70'}`}
              style={{ width: `${ammoPct}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1 text-[10px] font-mono mb-3">
          <div className="flex items-center gap-1">
            <span className="text-zinc-600 uppercase">Rng:</span>
            <span className="text-zinc-300">{w.range_km}km</span>
          </div>
          {w.rate_per_min && (
            <div className="flex items-center gap-1">
              <span className="text-zinc-600 uppercase">RoF:</span>
              <span className="text-zinc-300">{w.rate_per_min}/min</span>
            </div>
          )}
        </div>

        {w.notes && (
          <p className="text-[10px] text-zinc-500 leading-tight mb-3 line-clamp-2">{w.notes}</p>
        )}

        <button
          onClick={() => onToggle(w)}
          className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider transition-colors ${
            w.status === 'online'
              ? 'border-zinc-800 hover:border-orange-700/60 text-zinc-400 hover:text-orange-400'
              : 'border-zinc-800 hover:border-emerald-700/60 text-zinc-400 hover:text-emerald-400'
          }`}
          data-testid={`weapon-toggle-${w.id}`}
        >
          <Power size={11} />
          {w.status === 'online' ? 'Take Offline' : 'Bring Online'}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="weapon-systems-panel">
      <div className="flex items-center gap-2">
        <Activity size={16} className="text-sky-500" />
        <h2 className="text-sm font-bold uppercase tracking-wider">Weapon Systems</h2>
        <span className="text-xs text-zinc-600 font-mono">
          {weapons.filter(w => w.status === 'online').length}/{weapons.length} online
        </span>
      </div>

      {hard.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-red-500/80 mb-2">Hard Kill</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {hard.map(renderCard)}
          </div>
        </div>
      )}

      {soft.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-sky-500/80 mb-2">Soft Kill</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {soft.map(renderCard)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Countermeasures() {
  const [countermeasures, setCountermeasures] = useState([]);
  const [drones, setDrones] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    drone_detection_id: '',
    measure_type: 'jamming'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [measuresRes, dronesRes, weaponsRes] = await Promise.all([
        axios.get('/countermeasures'),
        axios.get('/drones'),
        axios.get('/weapon-systems')
      ]);
      setCountermeasures(measuresRes.data);
      setDrones(dronesRes.data.filter(d => d.status === 'active'));
      setWeapons(weaponsRes.data);
    } catch (error) {
      toast.error('Failed to load countermeasures');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWeapon = async (weapon) => {
    const next = weapon.status === 'online' ? 'offline' : 'online';
    try {
      const res = await axios.patch(`/weapon-systems/${weapon.id}`, { status: next });
      setWeapons(weapons.map(w => (w.id === weapon.id ? res.data : w)));
      toast.success(`${weapon.short_name} → ${next.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to update ${weapon.short_name}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/countermeasures', formData);
      toast.success('Countermeasure initiated');
      setOpen(false);
      setFormData({
        drone_detection_id: '',
        measure_type: 'jamming'
      });
      loadData();
    } catch (error) {
      toast.error('Failed to initiate countermeasure');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING COUNTERMEASURES...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="countermeasures-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase">Countermeasures</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Active mitigation systems
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-900/50 hover:bg-amber-900 text-amber-200 border border-amber-800 rounded-none uppercase text-xs" data-testid="initiate-countermeasure-button">
              <Plus size={16} className="mr-2" />
              Initiate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">Initiate Countermeasure</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="countermeasure-form">
              <div>
                <Label className="text-xs font-mono text-zinc-500 uppercase">Target Drone</Label>
                <Select
                  value={formData.drone_detection_id}
                  onValueChange={(value) => setFormData({ ...formData, drone_detection_id: value })}
                  required
                >
                  <SelectTrigger className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none" data-testid="target-drone-select">
                    <SelectValue placeholder="Select drone..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {drones.map((drone) => (
                      <SelectItem key={drone.id} value={drone.id}>
                        {drone.drone_id} - {drone.domain} ({drone.threat_level})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono text-zinc-500 uppercase">Measure Type</Label>
                <Select
                  value={formData.measure_type}
                  onValueChange={(value) => setFormData({ ...formData, measure_type: value })}
                >
                  <SelectTrigger className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none" data-testid="measure-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="jamming">RF Jamming</SelectItem>
                    <SelectItem value="gps-spoofing">GPS Spoofing</SelectItem>
                    <SelectItem value="net-capture">Net Capture</SelectItem>
                    <SelectItem value="directed-energy">Directed Energy Weapon (DEW)</SelectItem>
                    <SelectItem value="anti-drone-gun">Anti Drone Gun</SelectItem>
                    <SelectItem value="kinetic">Kinetic Interception</SelectItem>
                    <SelectItem value="cyber">Cyber Takeover</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full bg-amber-900/50 hover:bg-amber-900 text-amber-200 border border-amber-800 rounded-none uppercase text-xs"
                data-testid="submit-countermeasure-button"
              >
                Initiate Countermeasure
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <WeaponSystemsPanel weapons={weapons} onToggle={handleToggleWeapon} />

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-amber-500" />
          <h2 className="text-sm font-bold uppercase tracking-wider">Active Deployments</h2>
          <span className="text-xs text-zinc-600 font-mono">{countermeasures.length}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {countermeasures.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-zinc-600 font-mono text-sm">
            No countermeasures deployed
          </div>
        ) : (
          countermeasures.map((measure) => (
            <div
              key={measure.id}
              className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6 hover:border-zinc-700 transition-colors"
              data-testid={`countermeasure-${measure.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-center justify-center flex-shrink-0">
                  {measure.status === 'initiated' ? (
                    <Zap size={24} className="text-amber-500 animate-pulse" />
                  ) : (
                    <Shield size={24} className="text-emerald-500" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold uppercase tracking-tight">
                      {measure.measure_type}
                    </h3>
                    <span
                      className={`text-xs font-mono uppercase px-2 py-1 rounded-sm ${
                        measure.status === 'initiated'
                          ? 'bg-amber-900/50 text-amber-300 border border-amber-800'
                          : 'bg-emerald-900/50 text-emerald-300 border border-emerald-800'
                      }`}
                    >
                      {measure.status}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm text-zinc-400">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-600 font-mono text-xs uppercase">Initiated by:</span>
                      <span>{measure.initiated_by}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-600 font-mono text-xs uppercase">Time:</span>
                      <span>{new Date(measure.initiated_at).toLocaleString()}</span>
                    </div>
                    {measure.effectiveness && (
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-600 font-mono text-xs uppercase">Effectiveness:</span>
                        <span>{measure.effectiveness}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        </div>
      </div>
    </div>
  );
}